import { MemberImportResponse, OrganizationService } from '@app/_services';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Member, MembersStateModel } from './members.models';
import { DialogActions, MemberActions, MemberGroup, OrganizationActions, OrganizationState, ToastActions } from '@app/_store';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { compose, patch, updateItem, removeItem, insertItem, append } from '@ngxs/store/operators';
import { Navigate } from '@ngxs/router-plugin';
import { groupBy } from '@app/_helpers/utilities';

export const defaults = {
  import: {
    data: null,
    result: null,
  },
  list: [],
  groups: [],
};

@State<MembersStateModel>({
  name: 'members',
  defaults,
})
@Injectable()
export class MembersState {
  constructor(
    private organizationService: OrganizationService,
    private store: Store
  ) {}

  @Selector()
  static getMembers(state: MembersStateModel): Member[] {
    return state.list;
  }

  @Selector()
  static getGroups(state: MembersStateModel): MemberGroup[] {
    return state.groups;
  }

  @Selector()
  static import(state: MembersStateModel): {
    data?: FormData;
    result: MemberImportResponse;
  } {
    return state.import;
  }

  static member(id: string) {
    return createSelector([MembersState.getMembers], (state: Member[]) => {
      return state.find(s => s.id == id);
    });
  }

  @Selector()
  static getTags(state: MembersStateModel): string[] {
    return []
      .concat(...state.list.map(r => r.tags).filter(t => t))
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
  }

  @Action(MemberActions.Validate)
  validate(ctx: StateContext<MembersStateModel>): Observable<void> {
    const organization = this.store.selectSnapshot(OrganizationState.current);

    const emailIsValid = (email: string) => email && email.length > 0;
    const mobileIsValid = (mobile: string) => mobile && mobile.length > 0;

    const paymentRecipientIsValid = organization.smsEnabled
      ? (member: Member) => emailIsValid(member.email) && mobileIsValid(member.mobile)
      : (member: Member) => emailIsValid(member.email);

    const isMissingPaymentRecipient = (group: MemberGroup) => {
      const activeMembers = group.members.filter(m => !m.ignoreInActivities);
      return activeMembers.length > 0 && activeMembers.filter(m => m.isPaymentRecipient).length === 0;
    };

    const hasConfigurationErrors = (group: MemberGroup) => {
      return group.members.filter(m => m.isPaymentRecipient && !paymentRecipientIsValid(m)).length > 0;
    };

    const updates = ctx.getState().groups.map(rn =>
      updateItem<MemberGroup>(
        p => rn.id == p.id,
        patch<MemberGroup>({
          isMissingPaymentRecipient: isMissingPaymentRecipient(rn),
          memberWithConfigurationError: hasConfigurationErrors(rn),
        })
      )
    );

    ctx.setState(
      patch<MembersStateModel>({
        groups: compose(...updates),
      })
    );
    return of(null);
  }

  @Action(MemberActions.LoadAll)
  loadMembers(ctx: StateContext<MembersStateModel>): Observable<void> {
    const organizationId = this.store.selectSnapshot(OrganizationState.current).id;

    return this.organizationService.getMembers(organizationId).pipe(
      map(response => {
        const groupedMembers = groupBy(response.members, m => m.group);

        const groups = Object.keys(groupedMembers).map(key => {
          const uniqueNames = [
            ...new Set(
              groupedMembers[key].map(obj => {
                const parts = obj.name.split(' ');
                return parts[parts.length - 1].toLowerCase();
              })
            ),
          ];
          return {
            id: key,
            names: uniqueNames,
            members: groupedMembers[key],
          } as MemberGroup;
        });

        ctx.patchState({
          list: response.members,
          groups,
        });
      }),
      tap(() => {
        ctx.dispatch(new MemberActions.Validate());
      })
    );
  }

  @Action(MemberActions.Reset)
  reset(ctx: StateContext<MembersStateModel>): Observable<void> {
    ctx.patchState(defaults);
    return of(null);
  }

  @Action(MemberActions.Import)
  importMembers(ctx: StateContext<MembersStateModel>, action: MemberActions.Import): Observable<void> {
    if (action.commit === false) {
      ctx.patchState({
        import: {
          data: action.data,
          result: null,
        },
      });
    }

    return this.organizationService.importMembers(action.data, action.commit).pipe(
      map(response => {
        const state = ctx.getState();
        if (action.commit) {
          const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

          ctx.patchState({
            import: {
              data: null,
              result: null,
            },
          });
          ctx.dispatch(new OrganizationActions.ClearImportSuggestion());
          ctx.dispatch(new MemberActions.LoadAll());
          ctx.dispatch(new MemberActions.ClearImport());
          ctx.dispatch(new ToastActions.ShowSuccess('Organisationens personer har uppdaterats'));
          ctx.dispatch(new Navigate(['organization', currentOrganization.id, 'members']));
        } else {
          ctx.patchState({
            import: {
              ...state.import,
              result: response,
            },
          });
        }
        return response;
      }),
      map(response => {
        if (response.containsMemberWithMissingDetails) {
          ctx.dispatch(
            new DialogActions.Confirm(
              'Information saknas!',
              'Filen innehåller medlemmar som kommer att vara betalningsmottagare men saknar information som Klubbavgift behöver för att kommunicera. Dessa medlemmar kommer att missa information som skickas via Klubbavgift om du inte åtgärdar dessa.',
              () => of(null),
              false,
              'Okej!',
              false
            )
          );
        }
      }),
      catchError(_ => {
        ctx.dispatch(
          new ToastActions.ShowError(
            'Det uppstod ett fel vid importen. Kontrollera att filen följer anvisningarna för respektive källa.'
          )
        );
        throw _;
      })
    );
  }

  @Action(MemberActions.ClearImport)
  clearImport(ctx: StateContext<MembersStateModel>): Observable<void> {
    ctx.patchState({
      import: {
        data: null,
        result: null,
      },
    });
    return of(null);
  }

  @Action(MemberActions.ChangeGroup)
  changeGroup(ctx: StateContext<MembersStateModel>, action: MemberActions.ChangeGroup): Observable<void> {
    return this.organizationService.changeMemberGroup(action.memberId, action.toGroupId).pipe(
      map(r => {
        const newGroupId = r.groupId;

        const groupExists = ctx.getState().groups.find(g => g.id == newGroupId);

        //update member with new groupId
        ctx.setState(
          patch<MembersStateModel>({
            list: updateItem<Member>(
              m => m.id == action.memberId,
              patch({
                group: r.groupId,
              })
            ),
          })
        );
        const member = ctx.getState().list.find(m => m.id == action.memberId);

        if (!groupExists) {
          //add new group
          ctx.setState(
            patch<MembersStateModel>({
              groups: compose(
                insertItem<MemberGroup>({
                  id: newGroupId,
                  members: [member],
                  names: [this.getLastNameFromMember(member)],
                  isMissingPaymentRecipient: false,
                  memberWithConfigurationError: false,
                }),
                updateItem<MemberGroup>(
                  g => g.id == action.fromGroupId,
                  patch({
                    members: removeItem<Member>(m => m.id == action.memberId),
                  })
                )
              ),
            })
          );
        } else {
          //moved to existing group
          ctx.setState(
            patch<MembersStateModel>({
              groups: compose(
                updateItem(
                  g => g.id == action.fromGroupId,
                  patch({
                    members: removeItem<Member>(m => m.id == action.memberId),
                  })
                ),
                updateItem(
                  g => g.id == newGroupId,
                  patch({
                    members: append<Member>([member]),
                  })
                )
              ),
            })
          );

          if (ctx.getState().groups.find(g => g.id == action.fromGroupId).members.length === 0) {
            ctx.setState(
              patch<MembersStateModel>({
                groups: removeItem(g => g.id == action.fromGroupId),
              })
            );
          }
        }

        const updates = [];

        if (ctx.getState().groups.find(g => g.id == action.fromGroupId)) {
          //update names on old and new group
          updates.push(
            updateItem<MemberGroup>(
              g => g.id == action.fromGroupId,
              patch({
                names: this.getUniqueLastNamesForGroup(ctx.getState().groups.find(g => g.id == action.fromGroupId)),
              })
            )
          );
        }

        updates.push(
          updateItem<MemberGroup>(
            g => g.id == newGroupId,
            patch({
              names: this.getUniqueLastNamesForGroup(ctx.getState().groups.find(g => g.id == newGroupId)),
            })
          )
        );

        ctx.setState(
          patch<MembersStateModel>({
            groups: compose(...updates),
          })
        );

        ctx.dispatch(new MemberActions.Validate());
      })
    );
  }

  private getLastNameFromMember(member: Member) {
    const parts = member.name.split(' ');
    return parts[parts.length - 1];
  }

  private getUniqueLastNamesForGroup(group: MemberGroup) {
    return [
      ...new Set(
        group.members.map(obj => {
          const parts = obj.name.split(' ');
          return parts[parts.length - 1];
        })
      ),
    ];
  }

  @Action(MemberActions.Update)
  update(ctx: StateContext<MembersStateModel>, action: MemberActions.Update): Observable<void> {
    const member = action.member;
    const groupId = member.group;

    return this.organizationService.updateMember(member.id, action.operations).pipe(
      map(response => {
        ctx.setState(
          patch<MembersStateModel>({
            list: updateItem<Member>(y => y.id === member.id, patch<Member>(response)),
            groups: updateItem<MemberGroup>(
              g => g.id === groupId,
              patch<MemberGroup>({
                // paymentRecipients: append([response]),

                members: compose(updateItem<Member>(m => m.id === member.id, patch<Member>(response))),
              })
            ),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(new MemberActions.Validate());
      })
    );
  }

  @Action(MemberActions.AddWallet)
  addWallet(ctx: StateContext<MembersStateModel>, action: MemberActions.AddWallet): Observable<void> {
    const member = action.member;
    const groupId = action.member.group;

    ctx.setState(
      patch<MembersStateModel>({
        list: updateItem<Member>(
          y => y.id === action.member.id,
          patch<Member>({
            isCreatingWallet: true,
          })
        ),
        groups: updateItem<MemberGroup>(
          g => g.id === groupId,
          patch<MemberGroup>({
            members: compose(
              updateItem<Member>(
                m => m.id === member.id,
                patch<Member>({
                  isCreatingWallet: true,
                })
              )
            ),
          })
        ),
      })
    );

    return this.organizationService.addWallet(action.member.id, action.initialBalance).pipe(
      map(() => {
        console.log('got here');
        ctx.dispatch(new ToastActions.ShowSuccess(`Ett medlemskonto skapas för ${member.name}`));
      })
    );
  }

  @Action(MemberActions.LoadWalletTransactions)
  loadWalletTransactions(ctx: StateContext<MembersStateModel>, action: MemberActions.LoadWalletTransactions) {
    return this.organizationService.getTransactions(action.member.id).pipe(
      map(response => {
        ctx.setState(
          patch<MembersStateModel>({
            list: updateItem<Member>(
              y => y.id === action.member.id,
              patch<Member>({
                walletTransactions: response.transactions.map(transaction => {
                  return {
                    ...transaction,
                    timestamp: new Date(transaction.timestamp),
                  };
                }),
              })
            ),
          })
        );
      })
    );
  }

  @Action(MemberActions.ClearTransactions)
  clearTransactions(ctx: StateContext<MembersStateModel>, action: MemberActions.ClearTransactions) {
    ctx.setState(
      patch<MembersStateModel>({
        list: updateItem<Member>(
          y => y.id === action.member.id,
          patch<Member>({
            walletTransactions: undefined,
          })
        ),
      })
    );
  }

  @Action(MemberActions.UpdateWalletBalance)
  updateWalletBalance(ctx: StateContext<MembersStateModel>, action: MemberActions.UpdateWalletBalance) {
    const organization = this.store.selectSnapshot(OrganizationState.current);
    if (organization?.id != action.organizationId) return;

    ctx.setState(
      patch<MembersStateModel>({
        list: updateItem<Member>(
          y => y.id === action.memberId,
          patch<Member>({
            isCreatingWallet: false,
            walletBalance: action.newBalance,
          })
        ),
        groups: updateItem<MemberGroup>(
          g => g.id === action.groupId,
          patch<MemberGroup>({
            members: compose(
              updateItem<Member>(
                m => m.id === action.memberId,
                patch<Member>({
                  isCreatingWallet: false,
                  walletBalance: action.newBalance,
                })
              )
            ),
          })
        ),
      })
    );
  }
}
